import React from 'react';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import { debounce } from 'lodash';
import moment from 'moment';

import { QueryStringContext } from 'contexts/QueryStringContext';
import { SettingsOptionsContext } from 'contexts/SettingsOptionsContext';
import DownloadProgressBar from 'Data/DownloadData/DownloadProgressBar';
import {
	getISOString,
	getTailSpendAnalysisConfigurationForSave,
	validateTailSpend,
} from '../../Settings/utils';
import {
	getTailSpendAnalysisData,
	generateTailSpendAnalysisFile,
} from 'utils/apiRequests';
import { getCurrencySymbolFromOptions } from 'Details/utils';
import { Section } from '../Section';
import { TailSpendThreshold } from './TailSpendThreshold';
import { TailSpendPreview } from './TailSpendPreview';
import { TailSpendSegments } from './TailSpendSegments';
import { TailSpendRecurrence } from './TailSpendRecurrence';

import {
	ErrorLabel,
	Line,
	StyledApButtonSecondary,
} from './TailSpendSection.styles';

export const TAIL_SPEND_TYPES = {
	percentage: 0,
	value: 1,
	quantity: 2,
	vendorSpend: 3,
};

class TailSpendSectionClass extends React.Component {
	state = {};

	componentDidMount() {
		this.fetchPreviewData();
	}

	componentDidUpdate(prevProps) {
		if (!isEqual(prevProps.value, this.props.value)) {
			this.fetchPreviewDataIfNeeded(prevProps);
		}

		if (
			this.props.dateRangeId !== prevProps.dateRangeId ||
			!isEqual(this.props.previewDateRange, prevProps.previewDateRange)
		) {
			this.handleChange({});
		}
	}

	fetchPreviewDataIfNeeded = prevProps => {
		const tailSpendParams = this.getTailSpendParams();
		const oldTailSpendParams = prevProps.value
			? this.getTailSpendParams(prevProps)
			: prevProps.value;

		if (tailSpendParams && !isEqual(tailSpendParams, oldTailSpendParams)) {
			this.fetchPreviewData();
		}
	};

	getTailSpendParams(props = this.props) {
		const { value } = props;

		if (!value) {
			return null;
		}

		const {
			tailSpend,
			tailSpendType,
			tailSpendAnalysisConfiguration,
			useNetSpend,
		} = value;
		const { calculationType } = tailSpendAnalysisConfiguration;

		const currentPeriod = this.getSelectedDateRange(props);

		if (!currentPeriod) return null;

		const isValid =
			tailSpendType !== TAIL_SPEND_TYPES.percentage ||
			isEmpty(validateTailSpend(tailSpend));

		if (isValid) {
			const segments =
				getTailSpendAnalysisConfigurationForSave(
					value.tailSpendAnalysisConfiguration
				).segments || [];

			const tailSpendParams = {
				tailSpendValue: tailSpend,
				tailSpendType,
				dateFrom: currentPeriod.dateFrom
					? currentPeriod.dateFrom
					: getISOString(
							currentPeriod.monthPickerRange.from.year,
							currentPeriod.monthPickerRange.from.monthNumber
					  ),
				dateTo: currentPeriod.dateTo
					? currentPeriod.dateTo
					: getISOString(
							currentPeriod.monthPickerRange.to.year,
							currentPeriod.monthPickerRange.to.monthNumber,
							true
					  ),
				calculationType,
				segments,
				useNetSpend,
			};

			return tailSpendParams;
		}

		return null;
	}

	fetchPreviewData = debounce(() => {
		const tailSpendParams = this.getTailSpendParams();

		// tailSpendParams is null when invalid
		if (tailSpendParams) {
			Promise.all([
				getTailSpendAnalysisData({
					...tailSpendParams,
					harmonized: 'true',
				}),
				getTailSpendAnalysisData({
					...tailSpendParams,
					harmonized: 'false',
				}),
			]).then(([harmonized, original]) => {
				this.setState({
					previewData: {
						harmonized: this.getCompletePreviewData(
							harmonized,
							tailSpendParams.segments
						),
						original: this.getCompletePreviewData(
							original,
							tailSpendParams.segments
						),
					},
					segmentDownloadLinkEnabled:
						!!harmonized.segmentsData && harmonized.segmentsData.length > 0,
				});
			});
		}
	}, 1000);

	getCompletePreviewData = (data, segments) => {
		const { segmentsData } = data;

		return {
			...data,
			segmentsData: segments.map(({ name }) => {
				const found = segmentsData.find(item => item.title === name);

				if (found) {
					return found;
				}

				return {
					title: name,
					percentage: 0,
					value: 0,
					vendorsCount: 0,
				};
			}),
		};
	};

	handleChange = data => {
		const { value, onChange, harmonized } = this.props;
		const { tailSpendAnalysisConfiguration } = value;
		const selectedDateRange = this.getSelectedDateRange();
		const { dateFrom, dateTo } = selectedDateRange;

		const toUpdate = {
			...tailSpendAnalysisConfiguration,
			dateFrom,
			dateTo,
			harmonized,
		};

		if (
			Object.keys(data).length === 0 &&
			isEqual(toUpdate, tailSpendAnalysisConfiguration)
		) {
			return;
		}

		onChange({
			tailSpendAnalysisConfiguration: toUpdate,
			...data,
			tailSpendHasChanged: true,
		});
	};

	getSelectedDateRange = (props = this.props) => {
		const { dateRanges, dateRangeId, previewDateRange } = props;

		if (previewDateRange) {
			return previewDateRange;
		}

		const selectedDateRange =
			dateRanges.find(({ id }) => id === dateRangeId) || dateRanges[0];

		return selectedDateRange;
	};

	getDateRangeText() {
		const selectedDateRange = this.getSelectedDateRange();

		const { dateFrom, dateTo } = selectedDateRange;
		const f = 'MMM YYYY';
		return `${moment(dateFrom).format(f)} – ${moment(dateTo).format(f)}`;
	}

	handleSegmentsChange = updatedProps => {
		const { value, onChange, harmonized } = this.props;
		const { tailSpendAnalysisConfiguration } = value;

		const selectedDateRange = this.getSelectedDateRange();
		const { dateFrom, dateTo } = selectedDateRange;

		const toUpdate = {
			periodType: 1,
			periodValue: 12,
			...tailSpendAnalysisConfiguration,
			dateFrom,
			dateTo,
			harmonized,
			...updatedProps,
		};

		onChange({ tailSpendAnalysisConfiguration: toUpdate });
	};

	applySettingsAndGenerateTailSpendAnalysisFile = async () => {
		const { onApplySettings } = this.props;

		await onApplySettings();

		this.generateTailSpendAnalysisFile();
	};

	generateTailSpendAnalysisFile = () => {
		this.setState({
			showProgressBar: true,
			fileDownloadFailed: false,
		});

		generateTailSpendAnalysisFile()
			.then(() => {
				this.setState({
					fileDownloadFailed: false,
				});
			})
			.catch(() => {
				this.setState({
					fileDownloadFailed: true,
				});
			});
	};

	handleCloseProgressBar = () => {
		this.setState({
			showProgressBar: false,
		});
	};

	render() {
		const {
			animationIndex,
			value,
			currencySymbol,
			scaleSymbol,
			saveButtonDisabled,
			suppressSegmentsDownload,
			suppressVendorRecurrence,
		} = this.props;
		const {
			previewData,
			fileDownloadInProgress,
			segmentDownloadLinkEnabled,
			showProgressBar,
			fileDownloadFailed,
		} = this.state;
		const { tailSpendType, tailSpend, tailSpendAnalysisConfiguration } = value;

		const errors =
			tailSpendType === TAIL_SPEND_TYPES.percentage
				? validateTailSpend(tailSpend)
				: {};

		const {
			calculationType,
			periodType,
			periodValue,
		} = tailSpendAnalysisConfiguration;
		const needApplySettingsToDownload = !saveButtonDisabled;

		return (
			<Section
				title="Tail spend settings"
				description="Set tail spend threshold and configure tail spend segments. You can preview the effect on your spend data below."
				animationIndex={animationIndex}
			>
				<TailSpendThreshold
					previewData={previewData}
					onChange={this.handleChange}
					tailSpendType={tailSpendType}
					tailSpend={tailSpend}
					currencySymbol={currencySymbol}
					unit={scaleSymbol}
				/>
				<Line />
				{!suppressVendorRecurrence && (
					<React.Fragment>
						<TailSpendRecurrence
							onChange={this.handleSegmentsChange}
							periodType={periodType}
							periodValue={periodValue}
						/>
						<Line />
					</React.Fragment>
				)}

				<TailSpendSegments
					segments={value.tailSpendAnalysisConfiguration.segments || []}
					max={Number.POSITIVE_INFINITY}
					min={Number.NEGATIVE_INFINITY}
					currency={currencySymbol}
					unit={scaleSymbol}
					onChange={this.handleSegmentsChange}
					onValidationError={console.log}
					calculationType={calculationType}
				/>

				{previewData && (
					<TailSpendPreview
						previewData={previewData}
						currencySymbol={currencySymbol}
						dateRangeText={this.getDateRangeText()}
					/>
				)}

				{!suppressSegmentsDownload && (
					<StyledApButtonSecondary
						onClick={
							needApplySettingsToDownload
								? this.applySettingsAndGenerateTailSpendAnalysisFile
								: this.generateTailSpendAnalysisFile
						}
						disabled={!segmentDownloadLinkEnabled || fileDownloadInProgress}
						iconName="outline_save_alt"
					>
						{needApplySettingsToDownload
							? 'Apply settings and download tail spend analysis.xls'
							: 'Download tail spend analysis .xls'}
					</StyledApButtonSecondary>
				)}

				{showProgressBar && (
					<DownloadProgressBar
						error={fileDownloadFailed}
						successMessage="Generating file... You will receive a notification when the file has been generated"
						onClose={this.handleCloseProgressBar}
					/>
				)}

				{!isEmpty(errors) &&
					Object.keys(errors).map((key, index) => (
						<ErrorLabel key={index}>{errors[key]}</ErrorLabel>
					))}
			</Section>
		);
	}
}

export const TailSpendSection = props => (
	<SettingsOptionsContext.Consumer>
		{({ options }) => (
			<QueryStringContext.Consumer>
				{({ unit, currency, showHarmonizedVendors }) => {
					const currencySymbol = getCurrencySymbolFromOptions(
						options && options.currency,
						currency
					);

					return (
						<TailSpendSectionClass
							{...props}
							dateRanges={(options ? options.dateRanges : []) || []}
							scaleSymbol={unit || ''}
							currencySymbol={currencySymbol}
							harmonized={
								!!showHarmonizedVendors && showHarmonizedVendors !== 'false'
							}
						/>
					);
				}}
			</QueryStringContext.Consumer>
		)}
	</SettingsOptionsContext.Consumer>
);

export default TailSpendSection;
